import { marked } from "marked";
import hljs from "highlight.js";

// 配置 marked
const renderer = new marked.Renderer();

// 工具方法：转义 HTML
const escapeHtml = (input: unknown) => {
  const s = String(input ?? "");
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
};

// 自定义代码块渲染（容错：非字符串也能安全处理）
;(renderer as any).code = function (code: any, language?: string) {
  const source = typeof code === "string" ? code : String(code ?? "");
  const validLang = language && hljs.getLanguage(language) ? language : "plaintext";
  let highlighted = "";
  try {
    highlighted = hljs.highlight(source, { language: validLang }).value;
  } catch (_) {
    highlighted = escapeHtml(source);
  }

  return `<div class="code-block">
  </div>`;
};

renderer.codespan = function(code: any) {
  // 直接返回原文，不转 <code>
  const text = typeof code === "string" ? code : (code?.text || '');
  return escapeHtml(text);
};


// 自定义链接渲染（添加安全属性 + 兼容不同签名）
;(renderer as any).link = function (hrefOrToken: any, title?: any, text?: any) {
  // 新版 marked 传入的是 token 对象
  if (hrefOrToken && typeof hrefOrToken === 'object') {
    const token = hrefOrToken
    const href = escapeHtml(token.href || '')
    const ttl = escapeHtml(token.title || '')
    // 部分情况没有 text（例如 [](#anchor)），避免渲染 "undefined"
    const label = typeof text === 'string' ? text : ''
    return `<a href="${href}" title="${ttl}" target="_blank" rel="noopener noreferrer">${label}</a>`
  }
  // 旧版签名：href, title, text
  const href = escapeHtml(hrefOrToken || '')
  const ttl = escapeHtml(title || '')
  const label = typeof text === 'string' ? text : ''
  return `<a href="${href}" title="${ttl}" target="_blank" rel="noopener noreferrer">${label}</a>`
}

// 自定义表格渲染
;(renderer as any).table = function (header: string, body: string) {
  return `<div class="table-wrapper">
    <table class="markdown-table">
      <thead>${header || ''}</thead>
      <tbody>${body || ''}</tbody>
    </table>
  </div>`;
};

// 配置 marked 选项
marked.setOptions({
  renderer: renderer as any,
  langPrefix: "hljs language-",
  pedantic: false,
  gfm: true,
  breaks: true,
  // sanitize 已废弃，保持 false
  sanitize: false as any,
  smartypants: false,
  xhtml: false,
} as any);

/**
 * Markdown 渲染 composable
 */
export const useMarkdown = () => {
  /**
   * 🔧 新增：提取思考内容和正常内容
   * @param text 原始文本
   * @returns { thinking: string, content: string }
   */
  const extractThinking = (text: any): { thinking: string; content: string } => {
    if (text == null) return { thinking: '', content: '' };
    const str = typeof text === "string" ? text : String(text);
    
    let thinkingContent = '';
    let normalContent = str;
    
    // 检查是否有 <thinking> 标签
    const hasThinkingStart = /<thinking>/i.test(str);
    const hasThinkingEnd = /<\/thinking>/i.test(str);
    
    if (hasThinkingStart) {
      if (hasThinkingEnd) {
        // 如果有结束标签，提取思考内容，同时返回移除思考标签后的正文内容
        const thinkingMatch = str.match(/<thinking>([\s\S]*?)<\/thinking>/i);
        if (thinkingMatch) {
          thinkingContent = thinkingMatch[1].trim();
        }
        normalContent = str.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '').trim();
        return { thinking: thinkingContent, content: normalContent };
      } else {
        // 如果没有结束标签，提取从 <thinking> 到字符串结尾的内容（思考进行中）
        const thinkingMatch = str.match(/<thinking>([\s\S]*?)$/i);
        if (thinkingMatch) {
          thinkingContent = thinkingMatch[1].trim();
        }
        // 正常内容应该是思考标签之前的内容（通常为空）
        const beforeThinking = str.replace(/<thinking>[\s\S]*$/i, '').trim();
        return { thinking: thinkingContent, content: beforeThinking };
      }
    }
    
    // 处理 <think> 标签（逻辑相同）
    const hasRedactedStart = /<think>/i.test(str);
    const hasRedactedEnd = /<\/think>/i.test(str);
    
    if (hasRedactedStart) {
      if (hasRedactedEnd) {
        // 如果有结束标签，提取思考内容，同时返回移除思考标签后的正文内容
        const redactedMatch = str.match(/<think>([\s\S]*?)<\/think>/i);
        if (redactedMatch) {
          thinkingContent = redactedMatch[1].trim();
        }
        normalContent = str.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();
        return { thinking: thinkingContent, content: normalContent };
      } else {
        // 如果没有结束标签，提取从 <think> 到字符串结尾的内容（思考进行中）
        const redactedMatch = str.match(/<think>([\s\S]*?)$/i);
        if (redactedMatch) {
          thinkingContent = redactedMatch[1].trim();
        }
        // 正常内容应该是思考标签之前的内容（通常为空）
        const beforeRedacted = str.replace(/<think>[\s\S]*$/i, '').trim();
        return { thinking: thinkingContent, content: beforeRedacted };
      }
    }
    
    return { thinking: '', content: normalContent };
  };

  /**
   * 🔧 处理引号：区分对话（白色）和旁白（灰色）
   * @param text 原始文本
   * @returns 处理后的 HTML
   */
  const processQuotes = (text: string): string => {
    let result = '';
    let lastIndex = 0;
    
    // 匹配中文双引号 "xxx" 或英文双引号 "xxx"
    // " = \u201C (左双引号), " = \u201D (右双引号)
    // " = \u0022 (英文双引号)
    const quoteRegex = /[\u201C"][^\u201D"]*?[\u201D"]|"[^"]*?"/g;
    let match;
    
    while ((match = quoteRegex.exec(text)) !== null) {
      // 添加引号前的旁白部分（灰色）
      const narration = text.substring(lastIndex, match.index);
      if (narration) {
        result += `${narration}`;
      }
      
      // 添加引号内的对话部分（白色，保留引号）
      result += `<div class="dialogue">${match[0]}</div>`;
      
      lastIndex = match.index + match[0].length;
    }
    
    // 添加最后剩余的旁白部分（灰色）
    const remainingNarration = text.substring(lastIndex);
    if (remainingNarration) {
      result += `<div class="narration">${remainingNarration}</div>`;
    }
    
    return result || text;
  };

  /**
   * 将 Markdown 文本转换为 HTML
   * @param markdown Markdown 文本
   * @returns 渲染后的 HTML 字符串
   */
  const renderMarkdown = (markdown: any): string => {
    if (markdown == null) return "";
    let content = typeof markdown === "string" ? markdown : String(markdown);

    // 🔧 移除 <thinking> 和 <think> 标签（这些内容会在独立区域显示）
    content = content.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '');
    content = content.replace(/<think>[\s\S]*?<\/think>/gi, '');
    // 🔧 移除 <details> 标签及其内容
    content = content.replace(/<details\b[^>]*>[\s\S]*?<\/details>/gi, '');

    // 🔧 先处理引号标记
    content = processQuotes(content);

    try {
      return marked.parse(content) as string;
    } catch (error) {
      console.error("Markdown 渲染错误:", error);
      // 如果渲染失败，返回原始文本（转义 HTML）
      return escapeHtml(content);
    }
  };

  /**
   * 添加全局复制代码功能
   */
  const setupCopyCode = () => {
    if (typeof window !== 'undefined') {
      // 添加全局复制函数
      (window as any).copyCode = async (button: HTMLElement) => {
        const codeBlock = button.closest(".code-block");
        const code = codeBlock?.querySelector("code")?.textContent;

        if (code) {
          try {
            await navigator.clipboard.writeText(code);
            const originalText = button.textContent;
            button.textContent = "已复制!";
            button.classList.add("copied");

            setTimeout(() => {
              button.textContent = originalText;
              button.classList.remove("copied");
            }, 2000);
          } catch (error) {
            console.error("复制失败:", error);
            // 降级方案：选中文本
            const range = document.createRange();
            const selection = window.getSelection();
            const codeElement = codeBlock?.querySelector("code");
            if (codeElement && selection) {
              range.selectNodeContents(codeElement);
              selection.removeAllRanges();
              selection.addRange(range);
            }
          }
        }
      };
    }
  };

  return {
    renderMarkdown,
    extractThinking,
    setupCopyCode,
  };
};
